const Delay = require('../../utils/delay');
const CreateTwilioService = require('../twilio');
const { isAppForeground } = require('../../utils/app-visibility');

const internals = {};
const SERVICE_CACHE_KEY = Symbol.for('nearpeer.communication.service');

module.exports = (context) => {

    // CAUTION: Delay is required to avoid unread messages tag glitch on send message
    const MESSAGE_REFRESH_DELAY__IN_MILLIS = 200;
    const CURRENT_ANNOUNCEMENT_POLL_INTERVAL = 10000;
    const DUPLICATE_EVENT_WINDOW_MS = 1500;

    const transport = CreateTwilioService.fromContext(context);
    const nearpeerApi = context.api.nearpeer;
    const actions = context.actions;
    const selectors = context.selectors.all;
    const redux = context.redux.hooks;

    let listeningStarted = false;
    let globalListeningVersion = 0;
    let globalListeningPromise = null;
    let currentChannelSid = null;
    let announcementMessagesPollInterval = null;
    let announcementConversationsPollInterval = null;
    let announcementConversationsRefreshPromise = null;
    let isBootstrappingGlobalListening = false;
    const recentEventTimestamps = new Map();
    const recentSelfAuthoredMessageSyncs = new Map();

    const getLastMessagePreview = (channelSid) => selectors.getChannelLastMessage(
        redux.getState(),
        internals.normalizePreviewChannelSid(channelSid)
    );
    const getKnownChannel = (channelSid) => {

        const normalizedChannelSid = internals.normalizePreviewChannelSid(channelSid);

        return normalizedChannelSid
            ? redux.getState()?.dataFetching?.entities?.channels?.[normalizedChannelSid] || null
            : null;
    };

    const refreshMessages = () => Promise.resolve(currentChannelSid && redux.dispatch(actions.dataFetching.fetchMessages.firstPage({ channelSid: currentChannelSid })) || undefined);
    const refreshChannelLastMessage = (channelSid) => {

        const normalizedChannelSid = internals.normalizePreviewChannelSid(channelSid);

        return Promise.resolve(normalizedChannelSid && redux.dispatch(actions.dataFetching.fetchChannelLastMessage({
            channelSid: normalizedChannelSid
        })) || undefined);
    };
    const refreshLastMessages = () => redux.dispatch(actions.dataFetching.fetchAllChannelLastMessages());
    const refreshNotifications = () => redux.dispatch(actions.dataFetching.fetchNotifications());
    const syncConversationIfEnabled = ({ channelSid, messageSid = null, action = 'updated', author = null }) => {

        if (!internals.shouldUseFrontendConversationSync()) {
            return Promise.resolve(undefined);
        }

        const currentUserId = selectors.getCurrentUserId(redux.getState());

        if (!channelSid || !currentUserId || author !== currentUserId || !nearpeerApi?.communication?.syncConversation) {
            return Promise.resolve(undefined);
        }

        return nearpeerApi.communication.syncConversation({
            channelSid,
            messageSid,
            action,
            limit: 10
        });
    };
    const syncConversationResults = async (results, { action = 'updated', limit = 10 } = {}) => {

        if (!internals.shouldUseFrontendConversationSync() || !nearpeerApi?.communication?.syncConversation) {
            return [];
        }

        const channelSids = internals.getChannelSidsFromNormalizedResults(results);

        return await Promise.all(channelSids.map((channelSid) => (
            nearpeerApi.communication.syncConversation({
                channelSid,
                action,
                limit
            }).catch((error) => {

                console.warn('[communication] failed to sync conversation on load', {
                    channelSid,
                    action,
                    error
                });

                return null;
            })
        )));
    };

    const refreshConversationLists = () => Promise.all([
        fetchChannels(),
        refreshLastMessages(),
        refreshNotifications()
    ]);
    const shouldIgnoreBootstrapConversationEvent = () => isBootstrappingGlobalListening;
    const shouldSkipDuplicateEvent = (eventKey) => {

        const now = Date.now();
        const lastSeenAt = recentEventTimestamps.get(eventKey);

        recentEventTimestamps.forEach((seenAt, key) => {

            if (now - seenAt > DUPLICATE_EVENT_WINDOW_MS) {
                recentEventTimestamps.delete(key);
            }
        });

        if (typeof lastSeenAt === 'number' && (now - lastSeenAt) < DUPLICATE_EVENT_WINDOW_MS) {
            return true;
        }

        recentEventTimestamps.set(eventKey, now);
        return false;
    };
    const recordSelfAuthoredMessageSync = (channelSid) => {

        const normalizedChannelSid = internals.normalizePreviewChannelSid(channelSid);

        if (!normalizedChannelSid) {
            return;
        }

        recentSelfAuthoredMessageSyncs.set(normalizedChannelSid, Date.now());
    };
    const shouldSkipConversationSyncForRecentSelfAuthoredMessage = (channelSids = []) => {

        const now = Date.now();

        recentSelfAuthoredMessageSyncs.forEach((seenAt, channelSid) => {

            if (now - seenAt > DUPLICATE_EVENT_WINDOW_MS) {
                recentSelfAuthoredMessageSyncs.delete(channelSid);
            }
        });

        return channelSids.some((channelSid) => {

            const normalizedChannelSid = internals.normalizePreviewChannelSid(channelSid);
            const seenAt = recentSelfAuthoredMessageSyncs.get(normalizedChannelSid);

            return typeof seenAt === 'number' && (now - seenAt) < DUPLICATE_EVENT_WINDOW_MS;
        });
    };
const refreshChannelsIfUnknown = (channelSid) => {

        if (!channelSid || getKnownChannel(channelSid)) {
            return Promise.resolve(undefined);
        }

        return fetchChannels();
    };

    const refreshAnnouncementConversations = ({ force = false } = {}) => {

        if (announcementConversationsRefreshPromise && !force) {
            return announcementConversationsRefreshPromise;
        }

        announcementConversationsRefreshPromise = Promise.resolve().then(async () => {

            if (!internals.hasAnnouncementGroups(selectors.getGroups(redux.getState()))) {
                return undefined;
            }

            return redux.dispatch(actions.dataFetching.fetchAnnouncementConversations());
        }).finally(() => {

            announcementConversationsRefreshPromise = null;
        });

        return announcementConversationsRefreshPromise;
    };

    const updateMessage = (results) => redux.dispatch(actions.dataFetching.subscribeMessages.update(results));
    const updateChannelLastMessage = (channelSid, message) => {

        const normalizedChannelSid = internals.normalizePreviewChannelSid(channelSid);

        return redux.dispatch(actions.dataFetching.updateChannelLastMessage({
            channelSid: normalizedChannelSid,
            message
        }));
    };
    const removeMessage = ({ result: sid }) => redux.dispatch(actions.dataFetching.subscribeMessages.remove({ channelSid: currentChannelSid, sid }));
    const fetchChannel = () => redux.dispatch(actions.dataFetching.fetchChannel({ channelSid: currentChannelSid }));
    const fetchChannels = () => redux.dispatch(actions.dataFetching.fetchChannels());

    const ifSubscribedToAnnouncements = (func) => {

        if (internals.hasAnnouncementGroups(selectors.getGroups(redux.getState()))) {
            func();
        }
    };

    const fetchAnnouncementMessages = (id) => ifSubscribedToAnnouncements(() => redux.dispatch(actions.dataFetching.fetchAnnouncementMessages({ id })));

    const listeners = {
        onMessageAdded: async (results) => {

            const { result: messageSid, entities: { messages } } = results;
            const message = messages[messageSid];

            if (shouldSkipDuplicateEvent(`message-added:${messageSid}`)) {
                return undefined;
            }

            updateMessage(results);
            updateChannelLastMessage(message.channel, internals.toChannelLastMessagePreview(message));

            if (message.author === selectors.getCurrentUserId(redux.getState())) {
                recordSelfAuthoredMessageSync(message.channel);
            }

            await Delay(MESSAGE_REFRESH_DELAY__IN_MILLIS);

            await refreshMessages();

            Promise.all([
                refreshChannelsIfUnknown(message.channel),
                refreshNotifications(),
                syncConversationIfEnabled({
                    channelSid: message.channel,
                    messageSid,
                    action: 'added',
                    author: message.author
                })
            ]).then(() => {

                if (message.author === selectors.getCurrentUserId(redux.getState()) &&
                        message.body.indexOf('{{mention(') !== -1) {

                    return redux.dispatch(actions.communication.mention({
                        messageSid,
                        channelSid: message.channel
                    }));
                }

                return undefined;
            }).catch((error) => {

                console.warn('[communication] failed to finish message-added follow-up', {
                    channelSid: message.channel,
                    messageSid,
                    error
                });
            });
        },
        onMessageUpdated: async (results) => {

            const { result: messageSid, entities: { messages } = {} } = results;
            const message = messages?.[messageSid];

            if (shouldSkipDuplicateEvent(`message-updated:${messageSid}`)) {
                return undefined;
            }

            updateMessage(results);

            if (!message) {
                return;
            }

            const currentLastMessage = getLastMessagePreview(message.channel);

            if (internals.shouldUpdateLivePreview({
                currentLastMessage,
                message
            })) {
                updateChannelLastMessage(message.channel, internals.toChannelLastMessagePreview(message));
            }

            await syncConversationIfEnabled({
                channelSid: message.channel,
                messageSid,
                action: 'updated',
                author: message.author
            });
        },
        onMessageRemoved: async (results) => {

            const { result: messageSid, entities: { messages } = {} } = results;
            const message = messages?.[messageSid];

            if (shouldSkipDuplicateEvent(`message-removed:${messageSid}`)) {
                return undefined;
            }

            await syncConversationIfEnabled({
                channelSid: message?.channel,
                messageSid,
                action: 'removed',
                author: message?.author
            });

            return Promise.resolve(results)
                .then(removeMessage)
                .then(() => {

                    if (!message) {
                        return undefined;
                    }

                    const currentLastMessage = getLastMessagePreview(message.channel);

                    if (!internals.shouldRefreshPreviewAfterRemoval({
                        currentLastMessage,
                        message
                    })) {
                        return undefined;
                    }

                    return refreshChannelLastMessage(message.channel);
                });
        },
        onConversationAdded: async (results) => {

            if (shouldIgnoreBootstrapConversationEvent()) {
                return undefined;
            }

            const channelSids = internals.getChannelSidsFromNormalizedResults(results);
            if (channelSids.every((channelSid) => shouldSkipDuplicateEvent(`conversation-added:${channelSid}`))) {
                return undefined;
            }

            if (!shouldSkipConversationSyncForRecentSelfAuthoredMessage(channelSids)) {
                await syncConversationResults(results, { action: 'updated' });
            }

            return refreshConversationLists();
        },
        onConversationUpdated: async (results) => {

            if (shouldIgnoreBootstrapConversationEvent()) {
                return undefined;
            }

            const channelSids = internals.getChannelSidsFromNormalizedResults(results);
            if (channelSids.every((channelSid) => shouldSkipDuplicateEvent(`conversation-updated:${channelSid}`))) {
                return undefined;
            }

            if (!shouldSkipConversationSyncForRecentSelfAuthoredMessage(channelSids)) {
                await syncConversationResults(results, { action: 'updated' });
            }

            return refreshConversationLists();
        },
        onConversationRemoved: () => refreshConversationLists(),
        onConversationJoined: async (results) => {

            if (shouldIgnoreBootstrapConversationEvent()) {
                return undefined;
            }

            const channelSids = internals.getChannelSidsFromNormalizedResults(results);
            if (channelSids.every((channelSid) => shouldSkipDuplicateEvent(`conversation-joined:${channelSid}`))) {
                return undefined;
            }

            if (!shouldSkipConversationSyncForRecentSelfAuthoredMessage(channelSids)) {
                await syncConversationResults(results, { action: 'updated' });
            }

            return refreshConversationLists();
        },
        onConversationLeft: () => refreshConversationLists()
    };

    return {
        ...transport,
        startGlobalListening: async () => {

            if (listeningStarted) {
                return globalListeningPromise;
            }

            listeningStarted = true;
            isBootstrappingGlobalListening = true;
            const version = ++globalListeningVersion;
            const isCurrent = () => listeningStarted && version === globalListeningVersion;

            const promise = (async () => {

                try {
                    await transport.startListeningMessages({ listeners });

                    if (!isCurrent()) {
                        return;
                    }

                    await transport.getChannels();

                    if (!isCurrent()) {
                        return;
                    }

                    await fetchChannels();

                    await Promise.all([
                        refreshLastMessages(),
                        refreshNotifications()
                    ]);

                    if (!isCurrent()) {
                        return;
                    }

                    refreshAnnouncementConversations().catch((err) => {

                        console.error('Failed to fetch announcement conversations:', err);
                    });

                    announcementConversationsPollInterval = setInterval(() => {

                        if (!isAppForeground({ selectors, state: redux.getState() })) {
                            return;
                        }

                        refreshAnnouncementConversations().catch((err) => {

                            console.error('Failed to refresh announcement conversations:', err);
                        });
                    }, CURRENT_ANNOUNCEMENT_POLL_INTERVAL);

                    return undefined;
                }
                catch (error) {
                    if (isCurrent()) {
                        listeningStarted = false;
                        await transport.stopListeningMessages();
                    }

                    throw error;
                }
                finally {
                    if (version === globalListeningVersion) {
                        isBootstrappingGlobalListening = false;
                        globalListeningPromise = null;
                    }
                }
            })();

            globalListeningPromise = promise;
            return promise;
        },
        stopGlobalListening: async () => {

            if (!listeningStarted) {
                return Promise.resolve();
            }

            listeningStarted = false;
            globalListeningVersion += 1;
            globalListeningPromise = null;
            isBootstrappingGlobalListening = false;

            clearInterval(announcementMessagesPollInterval);
            clearInterval(announcementConversationsPollInterval);

            await transport.stopListeningMessages();
        },
        startListening: async (channelSid, { recover = false } = {}) => {

            const normalizedChannelSid = internals.normalizeChannelSid(channelSid);
            const isValidChannelSid = internals.isValidChannelSid(normalizedChannelSid);

            internals.updateCommunicationDebug({
                event: 'startListening',
                requestedChannelSid: channelSid,
                normalizedChannelSid,
                isValidChannelSid,
                previousChannelSid: currentChannelSid,
                listeningStarted,
                error: isValidChannelSid ? null : {
                    code: 'INVALID_CHANNEL_SID',
                    message: `Cannot start listening without a valid channel SID: ${channelSid}`
                }
            });

            if (!isValidChannelSid) {
                console.warn('[communication] skipping startListening for invalid channel SID', {
                    channelSid,
                    normalizedChannelSid
                });
                return Promise.resolve(false);
            }

            currentChannelSid = normalizedChannelSid;

            if (recover) {
                await transport.recoverConnection();
            }

            return await Promise.all([
                fetchChannel(),
                refreshMessages()
            ]).then((result) => {

                internals.updateCommunicationDebug({
                    event: 'startListening:success',
                    currentChannelSid,
                    isValidChannelSid: true
                });

                return result;
            }).catch((err) => {

                internals.updateCommunicationDebug({
                    event: 'startListening:error',
                    currentChannelSid,
                    isValidChannelSid: true,
                    error: {
                        code: err?.code,
                        message: err?.message,
                        stack: err?.stack
                    }
                });

                throw err;
            });
        },
        stopListening: async () => {

            internals.updateCommunicationDebug({
                event: 'stopListening',
                previousChannelSid: currentChannelSid,
                currentChannelSid: null,
                listeningStarted
            });

            if (!currentChannelSid) {
                return await true;
            }

            currentChannelSid = null;
            return await true;
        },
        getCurrentChannelSid: () => {

            return currentChannelSid;
        },
        startListeningAnnouncementMessages: (id) => {

            clearInterval(announcementMessagesPollInterval);
            currentChannelSid = null;

            announcementMessagesPollInterval = setInterval(
                () => {

                    if (isAppForeground({ selectors, state: redux.getState() })) {
                        fetchAnnouncementMessages(id);
                    }
                },
                CURRENT_ANNOUNCEMENT_POLL_INTERVAL
            );
        },
        stopListeningAnnouncementMessages: () => {

            clearInterval(announcementMessagesPollInterval);
        }
    };
};

module.exports.fromContext = (context) => {

    if (!context[SERVICE_CACHE_KEY]) {
        context[SERVICE_CACHE_KEY] = module.exports(context);
    }

    return context[SERVICE_CACHE_KEY];
};

internals.hasAnnouncementGroups = (groups) => !!groups?.find(({ isAnnouncement }) => isAnnouncement);

internals.isDebugEnabled = () => process.env.NEARPEER_COMMUNICATION_DEBUG === 'true';

// Twilio supports one service webhook URL, which points at production. Production
// therefore relies on the webhook processor while dev and staging retain /sync.
internals.shouldUseFrontendConversationSync = () => process.env.TWILIO_ENV !== 'production';

internals.normalizeChannelSid = (channelSid) => {
    if (channelSid === null || typeof channelSid === 'undefined') {
        return null;
    }

    const normalized = String(channelSid).trim();

    return normalized || null;
};

internals.normalizePreviewChannelSid = (channelSid) => {

    if (!channelSid) {
        return null;
    }

    if (typeof channelSid === 'string') {
        return channelSid;
    }

    if (typeof channelSid === 'object') {
        return channelSid.sid || channelSid.channelSid || null;
    }

    return String(channelSid);
};

internals.isValidChannelSid = (channelSid) => {
    const normalized = internals.normalizeChannelSid(channelSid);

    return /^CH[a-zA-Z0-9]+$/.test(normalized || '');
};

internals.updateCommunicationDebug = (details = {}) => {

    if (typeof window === 'undefined' || !internals.isDebugEnabled()) {
        return;
    }

    const current = window.__nearpeerCommunicationDebug || {};

    window.__nearpeerCommunicationDebug = {
        ...current,
        ...details,
        at: new Date().toISOString(),
        location: {
            pathname: window.location?.pathname,
            search: window.location?.search,
            hash: window.location?.hash
        }
    };

    window.dispatchEvent(new Event('nearpeer:client-debug:update'));
};

internals.toChannelLastMessagePreview = (message) => ({
    sid: message.sid,
    index: message.index,
    author: message.author,
    body: message.body,
    timestamp: message.timestamp
});

internals.getChannelSidsFromNormalizedResults = (results = {}) => {

    const { entities = {}, result = [] } = results;
    const channels = entities.channels || {};
    const values = Array.isArray(result) ? result : [result];

    return values.map((value) => {

        if (typeof value === 'string' && channels[value]?.sid) {
            return channels[value].sid;
        }

        if (value && typeof value === 'object') {
            return value.sid || value.channelSid || null;
        }

        return typeof value === 'string' ? value : null;
    }).filter(Boolean);
};

internals.toComparableTime = (value) => {

    const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();

    return Number.isNaN(timestamp) ? null : timestamp;
};

internals.shouldUpdateLivePreview = ({
    currentLastMessage,
    message
}) => {

    if (!message) {
        return false;
    }

    if (!currentLastMessage) {
        return true;
    }

    if (typeof currentLastMessage.index === 'number' && typeof message.index === 'number') {
        return message.index >= currentLastMessage.index;
    }

    const currentTimestamp = internals.toComparableTime(currentLastMessage.timestamp);
    const messageTimestamp = internals.toComparableTime(message.timestamp);

    if (currentTimestamp === null || messageTimestamp === null) {
        return false;
    }

    return messageTimestamp >= currentTimestamp;
};

internals.shouldRefreshPreviewAfterRemoval = ({
    currentLastMessage,
    message
}) => {

    if (!message || !currentLastMessage) {
        return false;
    }

    if (currentLastMessage.sid && message.sid) {
        return currentLastMessage.sid === message.sid;
    }

    if (typeof currentLastMessage.index === 'number' && typeof message.index === 'number') {
        return currentLastMessage.index === message.index;
    }

    const currentTimestamp = internals.toComparableTime(currentLastMessage.timestamp);
    const messageTimestamp = internals.toComparableTime(message.timestamp);

    if (currentTimestamp === null || messageTimestamp === null) {
        return false;
    }

    return currentTimestamp === messageTimestamp;
};
